import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAgenda } from "../../redux/actions/agenda.actions";
import { Box, Pagination } from "@mui/material";
import ClipLoader from "react-spinners/ClipLoader";
import { NormalText, TitleStyled, TooltipStyled } from "../../shared/styles";
import { ContactCard, ContainerActions, ContainerInfo } from "./styles";
import { Copy } from "@phosphor-icons/react";
import { copyToClipboard, formatLargeText } from "../../shared/utils";
import { Button } from "../../shared/button/button";
import { ModalDeleteContact } from "./modal-delete-contact";
import { ModalCreateEditContact } from "./modal-create-edit-contact";
import { Loader } from "../../shared/loader/loader";
import { useGetUser } from "../../hooks/useGetUser";

export const AgendaContainer = () => {
  const [showModals, setShowModals] = useState({
    createContact: false,
    editContact: false,
    deleteContact: false,
  });
  const [contactSelected, setContactSelected] = useState(null);

  const {
    data: agenda,
    isLoading: isLoadingAgenda,
    pagination: paginationAgenda,
    error,
  } = useSelector((state) => state.agenda);

  const { user } = useGetUser();

  const dispatch = useDispatch();

  function handleShowModal(modal) {
    setShowModals({ ...showModals, [modal]: !showModals[modal] });
  }

  function handleDeleteContact(contact) {
    setShowModals({ ...showModals, deleteContact: true });
    setContactSelected(contact);
  }

  function handleEditContact(contact) {
    setShowModals({ ...showModals, editContact: true });
    setContactSelected(contact);
  }

  useEffect(() => {
    if (user?.usuarioid) {
      dispatch(getAgenda(user?.usuarioid));
    }
  }, [dispatch, user?.usuarioid]);

  if (error)
    return (
      <NormalText color={"#EA7A7A"} size={14}>
        Error loading agenda
      </NormalText>
    );

  return (
    <Box>
      <ModalCreateEditContact
        open={showModals?.createContact || showModals?.editContact}
        handleClose={handleShowModal}
        idContact={contactSelected?.idwallet}
        contactData={contactSelected}
        isEdit={showModals?.editContact}
        userId={user?.usuarioid}
      />
      <ModalDeleteContact
        open={showModals?.deleteContact}
        handleClose={handleShowModal}
        idContact={contactSelected?.idwallet}
        userId={user?.usuarioid}
      />
      <Box display="flex" alignItems="center" gap={"24px"}>
        <TitleStyled>Agenda</TitleStyled>
        <Button
          label={
            isLoadingAgenda ? (
              <Loader size={16} color="#0197f6" />
            ) : (
              "Create contact"
            )
          }
          endIcon={!isLoadingAgenda && "PlusCircle"}
          onClick={() => handleShowModal("createContact")}
          buttonStyles={{ minWidth: "162px" }}
          containerStyles={{
            maxHeight: "39px",
          }}
        />
      </Box>
      <Box
        display={"flex"}
        flexWrap={"wrap"}
        gap={"8px"}
        mt={"24px"}
        justifyContent={"space-between"}
      >
        {isLoadingAgenda ? (
          <ClipLoader color={"#0197f6"} size={24} />
        ) : agenda.length > 0 ? (
          <>
            <Box display={"flex"} gap={"8px"} flexWrap={"wrap"}>
              {agenda.map((contact) => (
                <ContactCard key={contact.idwallet}>
                  <ContainerInfo>
                    <Box display={"flex"} gap={"24px"}>
                      <Box>
                        <NormalText>Alias</NormalText>
                        <Box display={"flex"} gap={"4px"} alignItems={"center"}>
                          <NormalText size={14} color={"#0197f6"} isBold>
                            {contact.alias || "-"}
                          </NormalText>
                          <TooltipStyled title="Copy" placement="top">
                            <Copy
                              color={"#0197f6"}
                              size={16}
                              onClick={() => copyToClipboard(contact.alias)}
                            />
                          </TooltipStyled>
                        </Box>
                      </Box>
                      <Box>
                        <NormalText>Email</NormalText>
                        <TooltipStyled
                          title={contact.email}
                          placement="top"
                          disableHoverListener={contact.email?.length < 20}
                        >
                          <NormalText size={14} color={"#272A24"}>
                            {formatLargeText(contact.email || "-", 20)}
                          </NormalText>
                        </TooltipStyled>
                      </Box>
                      <Box>
                        <NormalText>Name</NormalText>
                        <NormalText size={14} color={"#272A24"}>
                          {contact.nombre || "-"}
                        </NormalText>
                      </Box>
                      <Box>
                        <NormalText>Last name</NormalText>
                        <NormalText size={14} color={"#272A24"}>
                          {contact.apellido || "-"}
                        </NormalText>
                      </Box>
                    </Box>
                    <Box>
                      <NormalText>Address</NormalText>
                      <Box display={"flex"} gap={"8px"}>
                        <TooltipStyled
                          title={contact.walletadress}
                          placement="top"
                        >
                          <NormalText size={14} color={"#272A24"}>
                            {formatLargeText(contact.walletadress || "-", 40)}
                          </NormalText>
                        </TooltipStyled>
                        <TooltipStyled title="Copy" placement="top">
                          <Copy
                            color={"#8C8C8C"}
                            size={16}
                            onClick={() =>
                              copyToClipboard(contact.walletadress)
                            }
                          />
                        </TooltipStyled>
                      </Box>
                    </Box>
                  </ContainerInfo>
                  <ContainerActions>
                    <Button
                      type={"regular"}
                      iconColor={"#CBCBCB"}
                      startIcon={"PencilSimple"}
                      onClick={() => handleEditContact(contact)}
                      tooltipLabel={"Edit"}
                    />
                    <Button
                      type={"regular"}
                      iconColor={"#CBCBCB"}
                      startIcon={"Trash"}
                      onClick={() => handleDeleteContact(contact)}
                      tooltipLabel={"Delete"}
                    />
                  </ContainerActions>
                </ContactCard>
              ))}
            </Box>
            {paginationAgenda?.lastPage > 1 && (
              <Box
                display={"flex"}
                justifyContent={"center"}
                mt={"8px"}
                width={"100%"}
              >
                <Pagination
                  count={paginationAgenda?.lastPage}
                  page={paginationAgenda?.page}
                  onChange={(_, value) => {
                    return dispatch(getAgenda(user?.usuarioid, value));
                  }}
                  size="large"
                  sx={{ alignSelf: "center", marginTop: "8px" }}
                />
              </Box>
            )}
          </>
        ) : (
          <NormalText size={14}>No contacts found</NormalText>
        )}
      </Box>
    </Box>
  );
};
